import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Divider from 'LuxKit/Divider'
import Group from 'components/utils/Group'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import Tooltip from 'LuxKit/Tooltip'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import LoyaltyAccountTileCircularProgressBar from 'loyalty/components/LoyaltyAccountTileCircularProgressBar'
import LoyaltyDrawerTierCard from 'loyalty/components/LoyaltyDrawerTierCard'
import LoyaltyDrawerCongratulationsBanner from 'loyalty/components/LoyaltyDrawerCongratulationsBanner'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LoyaltyRewardsNotificationMobile from './LoyaltyRewardsNotificationMobile'
import LoyaltyInfiniteBrandLogo from 'loyalty/components/LoyaltyInfiniteBrandLogo'
import useLoyaltyRewardsNotification, {
  type LoyaltyRewardsConfig,
  closeLoyaltyRewardsNotification,
} from 'loyalty/hooks/useLoyaltyRewardsNotification'
import useSyncedCountUp, { type SyncedCountUpSpec } from 'loyalty/hooks/useSyncedCountUp'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import getTierBenefitsForProgression from 'loyalty/lib/getTierBenefitsForProgression'
import { BENEFIT_ICONS } from 'loyalty/constants/tierProgressionBenefits'
import TierProgressionBenefitTitle from 'loyalty/components/Banners/TierProgressionBenefitTitle'
import LoyaltyTierBadge from 'loyalty/components/LoyaltyTierBadge'
import RealisticConfetti from 'react-canvas-confetti/dist/presets/realistic'
import { CONFETTI_COLOURS } from 'loyalty/constants/loyaltyConfetti'
import eases from 'styles/tools/eases'
import zIndex from 'styles/tools/z-index'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import { clamp } from 'lib/maths/mathUtils'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'

const AUTO_DISMISS_MS = 12000
const ANIMATION_START_DELAY_MS = 400
const TIER_TRANSITION_DELAY_MS = 1600
/** When tier upgrade has no SC/nights delta, count-up is instant — reveal confetti/benefits sooner than `TIER_TRANSITION_DELAY_MS`. */
const TIER_UPGRADE_QUICK_TRANSITION_DELAY_MS = 600
/**
 * After `window` `load`, wait this long before scheduling the open sequence so hydration / first
 * paint can settle. SPA navigations skip this (`document.readyState === 'complete'`).
 */
const POST_LOAD_NOTIFICATION_DELAY_MS = 750
/**
 * Max wait for `requestIdleCallback` before starting the ring/value animation after the panel opens.
 */
const PROGRESS_ANIMATION_IDLE_TIMEOUT_MS = 800
/**
 * After the post-load delay, max wait for an idle slice before mounting the slide-in (cold load).
 */
const OPEN_NOTIFICATION_IDLE_TIMEOUT_MS = 1000
const LOAD_EVENT_FALLBACK_MS = 2500
const COUNT_UP_DURATION_MS = 500
const FALLBACK_TOP_PX = 88
const NOTIFICATION_TOP_GAP_PX = 10
const MAX_UPGRADE_BENEFITS = 3

const PositionAnchor = styled.span`
  position: absolute;
  top: 100%;
  inset-inline-end: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`

const FloatingContainer = styled.div`
  --loyalty-rewards-notification-offset: 120%;

  position: fixed;
  inset-inline-end: ${rem(4)};
  width: ${rem(375)};
  padding: ${rem(8)};
  max-width: calc(100vw - ${rem(32)});
  z-index: ${zIndex.notification};
  pointer-events: none;
  will-change: transform, opacity;
  transform: translateX(var(--loyalty-rewards-notification-offset));
  opacity: 0;
  transition: transform 0.3s ${eases.power2.out}, opacity 0.3s ${eases.circ.out};

  [dir="rtl"] & {
    --loyalty-rewards-notification-offset: -120%;
  }

  &.is-open {
    transform: translateX(0);
    opacity: 1;
  }

  ${mediaQueryUp.desktop} {
    inset-inline-end: ${rem(16)};
  }
`

const MobileFloatingContainer = styled.div`
  position: fixed;
  bottom: ${rem(16)};
  inset-inline: ${rem(16)};
  z-index: ${zIndex.notification};
  pointer-events: none;
  will-change: transform, opacity;
  transform: translateY(calc(100% + 32px));
  opacity: 0;
  transition: transform 0.3s ${eases.power2.out}, opacity 0.3s ${eases.circ.out};

  &.is-open {
    transform: translateY(0);
    opacity: 1;
  }
`

const NotificationCard = styled.div`
  background: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.M};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
  overflow: hidden;
  pointer-events: auto;
`

const GradientHeaderWrapper = styled.div`
  position: relative;
  padding: ${rem(16)} ${rem(16)} ${rem(20)};
  overflow: hidden;
`

const GradientLayer = styled.div`
  position: absolute;
  inset: 0;
  transition: opacity 0.4s ease-in-out;
  ${tierGradientBackgroundStyle}
`

const HeaderContent = styled.div`
  position: relative;
  z-index: ${zIndex.min};
`

const HeaderTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${rem(12)};
`

const HeaderBrandGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

const LoyaltyLogoWrapper = styled.div`
  ${tierNormalContrastColorStyle}
  transition: color 0.4s ease-in-out;
  line-height: 0;
`

const InfiniteLogoWrapper = styled.div`
  line-height: 0;
`

const GreetingHeading = styled(Heading)`
  ${tierNormalContrastColorStyle}
  transition: color 0.4s ease-in-out;

  &.tier-platinum {
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const ContentSection = styled.div`
  padding: ${rem(16)};
`

const PointsRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${rem(6)};
  margin-bottom: ${rem(16)};
`

const PointsValue = styled(Heading)`
  display: inline;
`

const ProgressBarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-self: stretch;
  margin-bottom: ${rem(16)};
`

const CircleDivider = styled.hr`
  border: none;
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  width: ${rem(28)};
  margin: 0;
`

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  line-height: 1;
`

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
`

const BenefitsRevealWrapper = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.5s ease-out;

  &.is-visible {
    grid-template-rows: 1fr;
  }
`

const BenefitsRevealInner = styled.div`
  overflow: hidden;
  padding-top: ${rem(4)};
`

const BenefitRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  padding: ${rem(2)} 0;
`

const BenefitRowStart = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  min-width: 0;
  flex: 1;

  > svg {
    flex-shrink: 0;
  }
`

const BenefitBadgeWrap = styled.div`
  flex-shrink: 0;
  min-width: ${rem(36)};
  display: flex;
  justify-content: flex-end;
`

function computeProgress(current: number, target: number): number {
  if (target === 0) return 100
  return clamp(0, Math.floor((current / target) * 100), 100)
}

function LoyaltyRewardsNotification() {
  const intl = useIntl()
  const [currentConfig, setCurrentConfig] = useState<LoyaltyRewardsConfig>()
  const [isOpen, setIsOpen] = useState(false)
  const [animationEnabled, setAnimationEnabled] = useState(false)
  const [isTierTransitioned, setIsTierTransitioned] = useState(false)
  const [confettiKey, setConfettiKey] = useState(0)
  const [platinumRetainShowConfetti, setPlatinumRetainShowConfetti] = useState(false)
  const [platinumRetainShowBanner, setPlatinumRetainShowBanner] = useState(false)
  const [containerTop, setContainerTop] = useState<number>(FALLBACK_TOP_PX)

  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoCloseEndTimeRef = useRef<number>(0)
  const autoCloseRemainingRef = useRef<number>(AUTO_DISMISS_MS)
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const anchorRef = useRef<HTMLSpanElement>(null)
  /** True after the panel has opened at least once for the current toast; used to ignore early closes (e.g. drawer before slide-in). */
  const notificationHasBeenVisibleRef = useRef(false)

  const prefersReducedMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    [],
  )

  const [loyaltyConfig] = useLoyaltyConfig()

  useLayoutEffect(() => {
    const onNotificationEvent = (e: Event) => {
      const customEvent = e as CustomEvent<LoyaltyRewardsConfig>
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current)
        resetTimerRef.current = null
      }
      notificationHasBeenVisibleRef.current = false
      setCurrentConfig(customEvent.detail)
      setAnimationEnabled(false)
      setIsOpen(false)
      setIsTierTransitioned(false)
      setPlatinumRetainShowConfetti(false)
      setPlatinumRetainShowBanner(false)
      setConfettiKey(k => k + 1)
    }
    document.addEventListener('loyalty-rewards-notification', onNotificationEvent)
    return () => document.removeEventListener('loyalty-rewards-notification', onNotificationEvent)
  }, [])

  useLoyaltyRewardsNotification()

  useEffect(() => {
    if (!currentConfig) {
      notificationHasBeenVisibleRef.current = false
    }
  }, [currentConfig])

  const closeAndReset = useCallback(() => {
    if (!notificationHasBeenVisibleRef.current) {
      return
    }
    setIsOpen(false)
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
    resetTimerRef.current = setTimeout(() => {
      setCurrentConfig(undefined)
      setAnimationEnabled(false)
      setIsTierTransitioned(false)
      setPlatinumRetainShowConfetti(false)
      setPlatinumRetainShowBanner(false)
      resetTimerRef.current = null
    }, 300)
  }, [])

  const flushClose = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current)
      autoCloseTimerRef.current = null
    }
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current)
      resetTimerRef.current = null
    }
    setIsOpen(false)
    setCurrentConfig(undefined)
    setAnimationEnabled(false)
    setIsTierTransitioned(false)
    setPlatinumRetainShowConfetti(false)
    setPlatinumRetainShowBanner(false)
  }, [])

  useEffect(() => () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
  }, [])

  useEffect(() => {
    document.addEventListener('close-loyalty-rewards-notification', closeAndReset)
    return () => document.removeEventListener('close-loyalty-rewards-notification', closeAndReset)
  }, [closeAndReset])

  useEffect(() => {
    if (!currentConfig) return
    let cancelled = false
    let openRaf = 0
    let idleCallbackId: number | undefined
    let openIdleCallbackId: number | undefined
    const timers: Array<ReturnType<typeof setTimeout>> = []

    /** Defer ring/value enable until an idle slice so SVG stroke transitions aren’t starved during long tasks. */
    const scheduleProgressAnimation = () => {
      if (cancelled) return
      if ('requestIdleCallback' in window) {
        idleCallbackId = window.requestIdleCallback(() => {
          idleCallbackId = undefined
          if (!cancelled) setAnimationEnabled(true)
        }, { timeout: PROGRESS_ANIMATION_IDLE_TIMEOUT_MS })
      } else if (!cancelled) {
        setAnimationEnabled(true)
      }
    }

    const runOpenSequence = () => {
      openRaf = requestAnimationFrame(() => {
        if (cancelled) return
        setIsOpen(true)
        notificationHasBeenVisibleRef.current = true
        const isTierUpgradeOpen = !!(currentConfig.isUpgrade && currentConfig.newTier)
        if (prefersReducedMotion) {
          setAnimationEnabled(true)
          if (isTierUpgradeOpen) {
            setIsTierTransitioned(true)
          }
          if (currentConfig.isPlatinumRetainCelebration && !currentConfig.isUpgrade) {
            setPlatinumRetainShowConfetti(false)
            setPlatinumRetainShowBanner(true)
          }
          return
        }
        /**
         * Tier-only upgrade: values animate toward thresholds (not real balances). The default path
         * waits ANIMATION_START_DELAY_MS + requestIdleCallback (up to ~1.2s) before count-up — that
         * feels like a dead pause on static numerators; start on the next task instead.
         */
        if (isTierUpgradeOpen) {
          timers.push(
            setTimeout(() => {
              if (!cancelled) setAnimationEnabled(true)
            }, 0),
          )
        } else {
          timers.push(setTimeout(scheduleProgressAnimation, ANIMATION_START_DELAY_MS))
        }
        if (isTierUpgradeOpen) {
          const noBalanceDelta =
            currentConfig.previousStatusCredits === currentConfig.currentStatusCredits
            && currentConfig.previousNights === currentConfig.currentNights
          const tierRevealDelayMs = noBalanceDelta
            ? TIER_UPGRADE_QUICK_TRANSITION_DELAY_MS
            : TIER_TRANSITION_DELAY_MS
          timers.push(setTimeout(() => setIsTierTransitioned(true), tierRevealDelayMs))
        }
        if (currentConfig.isPlatinumRetainCelebration && !currentConfig.isUpgrade) {
          timers.push(
            setTimeout(() => {
              setPlatinumRetainShowConfetti(true)
              setPlatinumRetainShowBanner(true)
            }, TIER_TRANSITION_DELAY_MS),
          )
        }
      })
    }

    /** Cold full page: wait for idle after post-load delay so the toast doesn’t open during long tasks. */
    const runOpenSequenceWhenIdle = () => {
      if (cancelled) return
      if ('requestIdleCallback' in window) {
        openIdleCallbackId = window.requestIdleCallback(() => {
          openIdleCallbackId = undefined
          if (!cancelled) runOpenSequence()
        }, { timeout: OPEN_NOTIFICATION_IDLE_TIMEOUT_MS })
      } else {
        runOpenSequence()
      }
    }

    /**
     * Full page load: wait for `load`, then post-load delay, then idle before open — smoother than
     * opening in the first busy frames after hydration.
     */
    if (document.readyState !== 'complete') {
      let started = false
      const startOnceAfterSettle = () => {
        if (cancelled || started) return
        started = true
        timers.push(setTimeout(() => {
          if (!cancelled) runOpenSequenceWhenIdle()
        }, POST_LOAD_NOTIFICATION_DELAY_MS))
      }
      const onLoad = () => {
        window.clearTimeout(fallbackTimer)
        window.removeEventListener('load', onLoad)
        startOnceAfterSettle()
      }
      const fallbackTimer = window.setTimeout(() => {
        window.removeEventListener('load', onLoad)
        if (cancelled || started) return
        started = true
        runOpenSequenceWhenIdle()
      }, LOAD_EVENT_FALLBACK_MS)
      window.addEventListener('load', onLoad)
      return () => {
        cancelled = true
        window.clearTimeout(fallbackTimer)
        window.removeEventListener('load', onLoad)
        cancelAnimationFrame(openRaf)
        timers.forEach(clearTimeout)
        if (idleCallbackId != null) window.cancelIdleCallback(idleCallbackId)
        if (openIdleCallbackId != null) window.cancelIdleCallback(openIdleCallbackId)
      }
    }

    runOpenSequence()
    return () => {
      cancelled = true
      cancelAnimationFrame(openRaf)
      timers.forEach(clearTimeout)
      if (idleCallbackId != null) window.cancelIdleCallback(idleCallbackId)
      if (openIdleCallbackId != null) window.cancelIdleCallback(openIdleCallbackId)
    }
  }, [currentConfig, prefersReducedMotion])

  useEffect(() => {
    if (!currentConfig) return
    const measure = () => {
      const rect = anchorRef.current?.getBoundingClientRect()
      if (rect && rect.bottom > 0 && rect.bottom < window.innerHeight * 0.55) {
        setContainerTop(Math.round(rect.bottom) + NOTIFICATION_TOP_GAP_PX)
      } else {
        setContainerTop(FALLBACK_TOP_PX)
      }
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [currentConfig])

  const startAutoCloseTimer = useCallback((duration: number = AUTO_DISMISS_MS) => {
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current)
    autoCloseRemainingRef.current = duration
    autoCloseEndTimeRef.current = Date.now() + duration
    autoCloseTimerRef.current = setTimeout(closeLoyaltyRewardsNotification, duration)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    startAutoCloseTimer()
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current)
        autoCloseTimerRef.current = null
      }
    }
  }, [isOpen, startAutoCloseTimer])

  const handleMouseEnter = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current)
      autoCloseTimerRef.current = null
    }
    autoCloseRemainingRef.current = Math.max(0, autoCloseEndTimeRef.current - Date.now())
  }, [])

  const handleMouseLeave = useCallback(() => {
    const remaining = autoCloseRemainingRef.current
    if (remaining > 0) {
      startAutoCloseTimer(remaining)
    } else {
      closeLoyaltyRewardsNotification()
    }
  }, [startAutoCloseTimer])

  const handleViewMembership = useCallback(() => {
    flushClose()
    closeLoyaltyRewardsNotification()
  }, [flushClose])

  const countUpSpec = useMemo((): SyncedCountUpSpec | null => {
    if (!currentConfig) return null
    return {
      points: { from: currentConfig.previousPoints, to: currentConfig.currentPoints },
      credits: {
        from: currentConfig.previousStatusCredits,
        to: currentConfig.currentStatusCredits,
      },
      nights: {
        from: currentConfig.previousNights,
        to: currentConfig.currentNights,
      },
    }
  }, [currentConfig])

  const animated = useSyncedCountUp(
    countUpSpec,
    animationEnabled && countUpSpec != null,
    COUNT_UP_DURATION_MS,
  )

  const confettiColours = useMemo(
    () => {
      if (currentConfig?.isUpgrade && currentConfig.newTier) {
        return CONFETTI_COLOURS[currentConfig.newTier]
      }
      if (currentConfig?.isPlatinumRetainCelebration && !currentConfig?.isUpgrade) {
        return CONFETTI_COLOURS.platinum
      }
      return []
    },
    [currentConfig?.isUpgrade, currentConfig?.newTier, currentConfig?.isPlatinumRetainCelebration],
  )
  const confettiDecoration = useCallback<NonNullable<React.ComponentProps<typeof RealisticConfetti>['decorateOptions']>>(
    (options) => ({
      ...options,
      colors: confettiColours,
      origin: { x: 0.88, y: 0.15 },
      zIndex: zIndex.notification,
    }),
    [confettiColours],
  )

  const closeAriaLabel = intl.formatMessage({
    id: 'q_o5Qw3u2v86TjJIWjph',
    defaultMessage: 'Close rewards notification',
    description: 'Aria label for close button on societe rewards notification',
  })
  const viewMembershipLabel = intl.formatMessage({
    id: 'oqnnD836QwxI8zJw6Xg9',
    defaultMessage: 'View membership',
    description: 'Link text to view loyalty membership from rewards notification',
  })
  const statusCreditsLabel = intl.formatMessage({
    id: '-EhO0xPNWFgohnsq2Oiy',
    defaultMessage: 'Status Credits',
    description: 'Label for status credits progress in loyalty account tile',
  })
  const nightsLabel = intl.formatMessage({
    id: 'x5UY9HsF8S3OtAjA4Q5D',
    defaultMessage: 'Nights',
    description: 'Form field label for nights progress in loyalty account tile',
  })
  const statusCreditsPendingTooltip = intl.formatMessage({
    id: 'X9h8QWFz-ORlSsqCB8u-',
    defaultMessage: 'Pending status credits may take up to 24hrs to appear',
    description: 'Tooltip explaining pending status credits delay in loyalty account tile',
  })
  const nightsPendingTooltip = intl.formatMessage({
    id: 'Z4G3K4vMzZnF3B4TNC2R',
    defaultMessage: 'Pending nights may take up to 24hrs to appear',
    description: 'Tooltip explaining pending nights delay in loyalty account tile',
  })
  const topBenefitsLabel = intl.formatMessage({
    id: 'Q1EO2NcxyI1uILATavuU',
    defaultMessage: 'Your top benefits',
    description: 'Section heading for top loyalty membership benefits',
  })
  const pointsAbbrevLabel = intl.formatMessage({
    id: 'DLJIxrZ4JoUzmTiyZSnk',
    defaultMessage: 'pts',
    description: 'Abbreviated points unit label displayed in the loyalty account menu tile',
  })
  const unlimitedLabel = intl.formatMessage({
    id: 'AToN7h6NCzEPABxzO-PR',
    defaultMessage: 'Unlimited',
    description: 'Badge text for an unlimited loyalty benefit in the Société rewards notification',
  })

  const getBenefitBadgeLabel = useCallback((benefit: App.LoyaltyRegionTierBenefit): string | undefined => {
    if (benefit.value?.unit === 'PERCENTAGE') return `${benefit.value.amount ?? 0}%`
    if (benefit.code === 'discounted_insurance' && benefit.value?.amount != null) {
      return `${benefit.value.amount}%`
    }
    if (benefit.unlimited) return unlimitedLabel
    const count = benefit.limit ?? 1
    if (count > 0) return `${count}x`
    return undefined
  }, [unlimitedLabel])

  if (!currentConfig) {
    return null
  }

  const isPlatinumRetainCelebration = !!currentConfig.isPlatinumRetainCelebration && !currentConfig.isUpgrade
  const isInfiniteEarnNotification = !!currentConfig.isInfiniteEarnNotification

  const displayedCreditsRemaining = Math.max(0, currentConfig.targetCredits - animated.credits)
  const displayedNightsRemaining = Math.max(0, currentConfig.targetNights - animated.nights)

  const { tier, firstName, pendingStatusCredits, pendingNights } = currentConfig

  const headerTier =
    currentConfig.isUpgrade && currentConfig.newTier && isTierTransitioned
      ? currentConfig.newTier
      : tier
  const headerTierClass = `tier-${headerTier}`

  const pointsDelta = currentConfig.currentPoints - currentConfig.previousPoints
  const creditsDelta = currentConfig.currentStatusCredits - currentConfig.previousStatusCredits
  const nightsDelta = currentConfig.currentNights - currentConfig.previousNights

  let greetingContent: React.ReactNode
  if (currentConfig.isUpgrade && currentConfig.newTier) {
    if (firstName) {
      greetingContent = (
        <FormattedMessage
          id="bg_WL6Vuzp1-kjCydHuw"
          defaultMessage="Congratulations {firstName}, you've unlocked {tierName}"
          description="Personalised header for Société rewards tier upgrade notification showing member name"
          values={{ firstName, tierName: getLoyaltyTierDisplayName(currentConfig.newTier) }}
        />
      )
    } else {
      greetingContent = (
        <FormattedMessage
          id="D_Rwl3cL2gfu4ZH5x6t-"
          defaultMessage="You've unlocked {tierName}"
          description="Anonymous header for Société rewards tier upgrade notification without member name"
          values={{ tierName: getLoyaltyTierDisplayName(currentConfig.newTier) }}
        />
      )
    }
  } else if (firstName) {
    greetingContent = (
      <FormattedMessage
        id="OPmI52A00iF03qyREwE1"
        defaultMessage="Congratulations {firstName}, your rewards have landed"
        description="Personalised header for Société rewards notification showing member first name"
        values={{ firstName }}
      />
    )
  } else {
    greetingContent = (
      <FormattedMessage
        id="CCYOlpnugjYGEw24VbVJ"
        defaultMessage="Your rewards have landed"
        description="Anonymous header for Société rewards notification when member name is not available"
      />
    )
  }

  const upgradeNewTier = currentConfig.isUpgrade ? currentConfig.newTier : undefined
  /** Top three benefits by `getTierBenefitsForProgression` order (compact toast). */
  const upgradeTierBenefits = upgradeNewTier
    ? getTierBenefitsForProgression(loyaltyConfig, upgradeNewTier).slice(0, MAX_UPGRADE_BENEFITS)
    : []

  const isUpgradeToast = !!currentConfig.isUpgrade && !!currentConfig.newTier
  const previousCreditsProgress = computeProgress(currentConfig.previousStatusCredits, currentConfig.targetCredits)
  const currentCreditsProgress = computeProgress(currentConfig.currentStatusCredits, currentConfig.targetCredits)
  const previousNightsProgress = computeProgress(currentConfig.previousNights, currentConfig.targetNights)
  const currentNightsProgress = computeProgress(currentConfig.currentNights, currentConfig.targetNights)
  /** Tier upgrade: rings follow `animated` actual balances vs new-tier targets (e.g. 10/600); avoids binary `animationEnabled` snap. */
  const creditsProgress = isUpgradeToast
    ? computeProgress(animated.credits, currentConfig.targetCredits)
    : animationEnabled ? currentCreditsProgress : previousCreditsProgress
  const nightsProgress = isUpgradeToast
    ? computeProgress(animated.nights, currentConfig.targetNights)
    : animationEnabled ? currentNightsProgress : previousNightsProgress

  const ringTier = isUpgradeToast && currentConfig.newTier ? currentConfig.newTier : tier
  const ringTransitionDuration = prefersReducedMotion ? '0s' : (isUpgradeToast ? '1.2s' : '0.5s')

  let bottomContent: React.ReactNode
  if (isInfiniteEarnNotification) {
    bottomContent = null
  } else if (currentConfig.isUpgrade && upgradeNewTier) {
    bottomContent = (
      <BenefitsRevealWrapper className={clsx({ 'is-visible': isTierTransitioned })}>
        <BenefitsRevealInner>
          <Group direction="vertical" gap={8}>
            <BodyText variant="small" weight="bold">{topBenefitsLabel}</BodyText>
            {upgradeTierBenefits.map((benefit, index) => {
              const icon = BENEFIT_ICONS[benefit.code]
              const badge = getBenefitBadgeLabel(benefit)
              return (
                <React.Fragment key={benefit.code}>
                  {index > 0 && <Divider kind="secondary" />}
                  <BenefitRow>
                    <BenefitRowStart>
                      {icon}
                      <BodyText variant="small">
                        <TierProgressionBenefitTitle benefit={benefit} variant="compact" />
                      </BodyText>
                    </BenefitRowStart>
                    <BenefitBadgeWrap>
                      {badge && (
                        <LoyaltyTierLabel kind="secondary" tier={upgradeNewTier}>
                          {badge}
                        </LoyaltyTierLabel>
                      )}
                    </BenefitBadgeWrap>
                  </BenefitRow>
                </React.Fragment>
              )
            })}
          </Group>
        </BenefitsRevealInner>
      </BenefitsRevealWrapper>
    )
  } else if (isPlatinumRetainCelebration) {
    bottomContent = (
      <BenefitsRevealWrapper className={clsx({ 'is-visible': platinumRetainShowBanner })}>
        <BenefitsRevealInner>
          <LoyaltyDrawerCongratulationsBanner tier="platinum" reviewDate={currentConfig.tierReviewDate} />
        </BenefitsRevealInner>
      </BenefitsRevealWrapper>
    )
  } else {
    bottomContent = (
      <LoyaltyDrawerTierCard
        tier={currentConfig.unlockCardTier}
        isRetain={currentConfig.isRetain}
        retainVariant={currentConfig.retainVariant}
        creditsRemaining={displayedCreditsRemaining}
        nightsRemaining={displayedNightsRemaining}
        tierReviewDate={currentConfig.tierReviewDate}
        membershipYearEndDate={currentConfig.membershipYearEndDate}
      />
    )
  }

  const desktopCard = (
    <NotificationCard
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <GradientHeaderWrapper>
        <GradientLayer
          className={`tier-${tier}`}
          style={{
            opacity:
              currentConfig.isUpgrade && currentConfig.newTier && isTierTransitioned ? 0 : 1,
          }}
        />
        {currentConfig.isUpgrade && currentConfig.newTier && (
          <GradientLayer
            className={`tier-${currentConfig.newTier}`}
            style={{ opacity: isTierTransitioned ? 1 : 0 }}
          />
        )}

        <HeaderContent>
          <HeaderTopRow>
            <HeaderBrandGroup>
              {isInfiniteEarnNotification && (
                <InfiniteLogoWrapper>
                  <LoyaltyInfiniteBrandLogo height={22} />
                </InfiniteLogoWrapper>
              )}
              {!isInfiniteEarnNotification && (
                <>
                  <LoyaltyLogoWrapper className={headerTierClass}>
                    <LoyaltyProgramNameLogo width={62} height={14} />
                  </LoyaltyLogoWrapper>
                  <LoyaltyTierBadge tier={headerTier} size="small" />
                </>
              )}
            </HeaderBrandGroup>
            <IconButton
              kind="tertiary"
              size="small"
              onClick={closeLoyaltyRewardsNotification}
              aria-label={closeAriaLabel}
            >
              <LineTimesIcon colour={headerTier === 'platinum' ? 'dark-contrast' : 'normal-contrast'} />
            </IconButton>
          </HeaderTopRow>

          <GreetingHeading variant="heading4" className={headerTierClass}>
            {greetingContent}
          </GreetingHeading>
        </HeaderContent>
      </GradientHeaderWrapper>

      <ContentSection>
        <Group direction="vertical" gap={16}>
          <PointsRow>
            <PointsValue variant="heading3">
              <FormatDecimal value={animated.points} />
            </PointsValue>
            <BodyText variant="small" colour="light-foreground-alt">{pointsAbbrevLabel}</BodyText>
            {pointsDelta > 0 && (
              <Caption variant="medium" colour="success" weight="bold">
                <FormattedMessage
                  id="qwGgvGRkiynXdSZsbJk3"
                  defaultMessage="+{amount} added"
                  description="Shows how many points were earned in the Société rewards notification"
                  values={{ amount: <FormatDecimal value={pointsDelta} /> }}
                />
              </Caption>
            )}
          </PointsRow>

          {!isInfiniteEarnNotification && (
            <ProgressBarsContainer>
              <Group direction="vertical" horizontalAlign="center" gap={4}>
                <LoyaltyAccountTileCircularProgressBar
                  key={confettiKey}
                  progress={creditsProgress}
                  tier={ringTier}
                  variant="light"
                  transitionDuration={ringTransitionDuration}
                >
                  <Group direction="vertical" horizontalAlign="center" gap={2}>
                    <BodyText variant="medium" weight="bold">
                      <FormatDecimal value={animated.credits} />
                    </BodyText>
                    <CircleDivider />
                    <Caption variant="small" colour="light-foreground-alt">
                      <FormatDecimal value={currentConfig.targetCredits} />
                    </Caption>
                  </Group>
                </LoyaltyAccountTileCircularProgressBar>
                <Group direction="vertical" horizontalAlign="center" gap={2}>
                  <BodyText variant="small" weight="bold">{statusCreditsLabel}</BodyText>
                  <Group direction="horizontal" gap={4} verticalAlign="center">
                    <Caption variant="medium" colour="light-foreground-alt">
                      <FormattedMessage
                        id="4XTLBdhy0Xunj_vOAlrB"
                        defaultMessage="{value} pending"
                        description="Pending status credits count in loyalty account tile"
                        values={{ value: <FormatDecimal value={pendingStatusCredits} /> }}
                      />
                    </Caption>
                    <Tooltip description={statusCreditsPendingTooltip} placement="bottom">
                      <InfoIconWrapper><LineInfoCircleIcon size={14} /></InfoIconWrapper>
                    </Tooltip>
                  </Group>
                  {creditsDelta > 0 && (
                    <Caption variant="medium" colour="success" weight="bold">
                      <FormattedMessage
                        id="oyChbERcE_Tw7laJsXDz"
                        defaultMessage="+{amount} added"
                        description="Shows how many status credits were earned in the Société rewards notification"
                        values={{ amount: <FormatDecimal value={creditsDelta} /> }}
                      />
                    </Caption>
                  )}
                </Group>
              </Group>

              <Group direction="vertical" horizontalAlign="center" gap={4}>
                <LoyaltyAccountTileCircularProgressBar
                  key={confettiKey}
                  progress={nightsProgress}
                  tier={ringTier}
                  variant="light"
                  transitionDuration={ringTransitionDuration}
                >
                  <Group direction="vertical" horizontalAlign="center" gap={2}>
                    <BodyText variant="medium" weight="bold">
                      <FormatDecimal value={animated.nights} />
                    </BodyText>
                    <CircleDivider />
                    <Caption variant="small" colour="light-foreground-alt">
                      <FormatDecimal value={currentConfig.targetNights} />
                    </Caption>
                  </Group>
                </LoyaltyAccountTileCircularProgressBar>
                <Group direction="vertical" horizontalAlign="center" gap={2}>
                  <BodyText variant="small" weight="bold">{nightsLabel}</BodyText>
                  <Group direction="horizontal" gap={4} verticalAlign="center">
                    <Caption variant="medium" colour="light-foreground-alt">
                      <FormattedMessage
                        id="kqEQcoVT8wzJzrD-xRhw"
                        defaultMessage="{value} pending"
                        description="Pending nights count shown below the nights progress ring in loyalty account tile"
                        values={{ value: <FormatDecimal value={pendingNights} /> }}
                      />
                    </Caption>
                    <Tooltip description={nightsPendingTooltip} placement="bottom">
                      <InfoIconWrapper><LineInfoCircleIcon size={14} /></InfoIconWrapper>
                    </Tooltip>
                  </Group>
                  {nightsDelta > 0 && (
                    <Caption variant="medium" colour="success" weight="bold">
                      <FormattedMessage
                        id="a4gu_Ap-UdtYyRbxoG9i"
                        defaultMessage="+{amount} added"
                        description="Shows how many nights were earned in the Société rewards notification"
                        values={{ amount: <FormatDecimal value={nightsDelta} /> }}
                      />
                    </Caption>
                  )}
                </Group>
              </Group>
            </ProgressBarsContainer>
          )}

          {bottomContent}

          <FooterActions>
            <TextButton
              kind="secondary"
              size="small"
              to={`/${LOYALTY_LANDING_PAGE}`}
              onClick={handleViewMembership}
            >
              {viewMembershipLabel}
            </TextButton>
          </FooterActions>
        </Group>
      </ContentSection>
    </NotificationCard>
  )

  return (
    <>
      <PositionAnchor ref={anchorRef} aria-hidden />

      {!prefersReducedMotion && (
        (currentConfig.isUpgrade && currentConfig.newTier && isTierTransitioned) ||
        (isPlatinumRetainCelebration && platinumRetainShowConfetti)
      ) && (
        <RealisticConfetti
          key={confettiKey}
          autorun={{ duration: 3, delay: 0, speed: 1 }}
          decorateOptions={confettiDecoration}
        />
      )}

      <CSSBreakpoint max="mobile" dynamic>
        <MobileFloatingContainer
          className={clsx({ 'is-open': isOpen })}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <LoyaltyRewardsNotificationMobile
            config={currentConfig}
            onViewMembership={handleViewMembership}
            onClose={closeLoyaltyRewardsNotification}
          />
        </MobileFloatingContainer>
      </CSSBreakpoint>

      <CSSBreakpoint min="tablet" dynamic>
        <FloatingContainer
          className={clsx({ 'is-open': isOpen })}
          style={{ top: containerTop }}
        >
          {desktopCard}
        </FloatingContainer>
      </CSSBreakpoint>
    </>
  )
}

export { closeLoyaltyRewardsNotification }
export default LoyaltyRewardsNotification
